import * as React from 'react';
import { useEffect } from 'react';
import { inject, observer } from 'mobx-react';

import { Context } from 'bernie-context';

import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkLayoutFlexItem, UitkLayoutFlex } from 'uitk-react-layout-flex';
import { UitkTertiaryButton } from 'uitk-react-button';
import { liveAnnounce, Level } from 'uitk-react-utils';

import { DatePicker, SearchFormButton } from '.';
import { Locations } from 'components/search/wizard/locations';
import { FlightsTripType } from '__generated__/api';
import { FlightsSearchFormJourneyCriteriaType } from 'types/search/wizard';
import { SearchFormStore } from 'src/stores';
import { flightsSimpleLocalizationTemplate } from 'utility';
import { focusElement } from 'utility/focus-element';
import { InteractableFieldComponentProps } from 'src/types/shared';

export interface JourneyProps extends InteractableFieldComponentProps {
  journey: FlightsSearchFormJourneyCriteriaType;
  index: number;
  context: Context;
  flightType: string;
  isMobile?: boolean;
  essEndpoint?: string;
  totalJourneys?: number;
  updateJourneyDates: (index: number, selectedDepartureDate: Date, selectedReturnDate: Date) => void;
  deleteFlight: (index: number) => void;
  searchFormStore?: SearchFormStore;
  doneAction?: string;
}

export const Journey = inject('searchFormStore')(
  observer(
    ({
      journey,
      index,
      context,
      flightType,
      isMobile,
      essEndpoint,
      totalJourneys,
      updateJourneyDates,
      deleteFlight,
      onFieldInteraction,
      searchFormStore
    }: JourneyProps) => {
      const {
        originLocation,
        destinationLocation,
        departureDate,
        returnDate,
        removeJourneyAction,
        swapLocationsAction
      } = journey;
      const { accessibilityUsageMessage } = removeJourneyAction;
      const isMdest = flightType === FlightsTripType.MULTI_DESTINATION;
      const isRT = flightType === FlightsTripType.ROUND_TRIP;
      const desktopRtOwSearchBtn = !isMobile && !isMdest;
      const locationInputContainerColSpan = isMobile ? 12 : isRT ? 7 : 8;
      const singleMode = !isRT;
      const dateClickAnalytics = departureDate.handleClickAnalytics && [
        departureDate.handleClickAnalytics,
        returnDate.handleClickAnalytics
      ];

      const onSubmitDatePicker = (startDate: Date, endDate: Date) => {
        updateJourneyDates(index, startDate, endDate);
      };

      const onClickRemoveHandler = () => {
        deleteFlight(index);
      };

      const [originRef, setOriginRef] = React.useState();
      const onSetOriginRef = (refData: React.Ref<HTMLButtonElement>) => setOriginRef(refData);

      const [destinationRef, setDestinationRef] = React.useState();
      const onSetDestinationRef = (refData: React.Ref<HTMLButtonElement>) => setDestinationRef(refData);

      const [datePickerRefs, setDatePickerRefs] = React.useState([]);
      const onSetDatePickerRefs = (refData: React.Ref<HTMLButtonElement>[]) => setDatePickerRefs(refData);

      const departureDateRef = datePickerRefs && datePickerRefs[0];
      const returnDateRef = datePickerRefs && datePickerRefs[1];

      const originError = originLocation.currentError;
      const destinationError = destinationLocation.currentError;
      const departureDateError = departureDate.currentError;
      const returnDateError = returnDate.currentError;

      const registerFirstJourneyError = (error: string, ref: React.Ref<HTMLElement>) => {
        searchFormStore.setFirstErrorRef(ref);
        liveAnnounce(error, Level.ASSERTIVE);
      };

      const checkJourneyErrors = () => {
        if (originError && originRef) {
          return registerFirstJourneyError(originError, originRef);
        }
        if (destinationError && destinationRef) {
          return registerFirstJourneyError(destinationError, destinationRef);
        }
        if (departureDateError && departureDateRef) {
          return registerFirstJourneyError(departureDateError, departureDateRef);
        }
        if (returnDateError && returnDateRef) {
          return registerFirstJourneyError(returnDateError, returnDateRef);
        }
      };

      const focusLastAddedTrip = refData => {
        const focus = focusElement;
        focus(refData);
        searchFormStore.shouldFocusLastAddedTrip = false;
      };

      useEffect(() => {
        originRef && searchFormStore.shouldFocusLastAddedTrip && focusLastAddedTrip(originRef);
        !searchFormStore.firstErrorRegistered && checkJourneyErrors();
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [
        searchFormStore.firstErrorRegistered,
        datePickerRefs,
        departureDateError,
        returnDateError,
        originRef,
        originError,
        destinationRef,
        destinationError
      ]);

      const removeJourneyA11yValue = (originLocation: string, destinationLocation: string): string => {
        if (!accessibilityUsageMessage || !originLocation || !destinationLocation) {
          return;
        }

        return flightsSimpleLocalizationTemplate(accessibilityUsageMessage, [originLocation, destinationLocation]);
      };

      return (
        <>
          <UitkLayoutGrid columns={12} space="three">
            <UitkLayoutGridItem colSpan={locationInputContainerColSpan}>
              <div>
                <Locations
                  journeyIndex={index}
                  originLocation={originLocation}
                  destinationLocation={destinationLocation}
                  context={context}
                  essEndpoint={essEndpoint}
                  onSetOriginRef={onSetOriginRef}
                  onSetDestinationRef={onSetDestinationRef}
                  onFieldInteraction={onFieldInteraction}
                  swapLocationsAction={swapLocationsAction}
                />
              </div>
            </UitkLayoutGridItem>
            <UitkLayoutGridItem
              colSpan={{
                small: 12,
                medium: isRT ? 12 : 6,
                large: isRT ? 5 : 4
              }}
            >
              <div>
                <UitkLayoutFlex space="three">
                  <UitkLayoutFlexItem grow={1}>
                    <div>
                      <DatePicker
                        startDate={departureDate}
                        endDate={returnDate}
                        flightType={flightType}
                        singleMode={singleMode}
                        identifier={`${flightType}-${index}`}
                        onSetDatePickerRefs={onSetDatePickerRefs}
                        onSubmit={onSubmitDatePicker}
                        buttonText={departureDate.doneAction}
                        tripIndex={index}
                        onClickAnalytics={dateClickAnalytics}
                        onFieldInteraction={onFieldInteraction}
                      />
                    </div>
                  </UitkLayoutFlexItem>
                  {desktopRtOwSearchBtn && (
                    <UitkLayoutFlexItem shrink={0} minWidth="twenty_four">
                      <div>
                        <SearchFormButton onFieldInteraction={onFieldInteraction} />
                      </div>
                    </UitkLayoutFlexItem>
                  )}
                </UitkLayoutFlex>
              </div>
            </UitkLayoutGridItem>
          </UitkLayoutGrid>
          {isMdest && totalJourneys !== 1 && (
            <UitkLayoutFlex className="all-t-margin-three s-b-margin-four m-b-margin-four">
              <UitkLayoutFlexItem>
                <UitkTertiaryButton
                  text={removeJourneyAction.displayAction}
                  onClick={onClickRemoveHandler}
                  data-test-id={`remove-trip-${index}`}
                  aria-label={removeJourneyA11yValue(originLocation.value, destinationLocation.value)}
                />
              </UitkLayoutFlexItem>
            </UitkLayoutFlex>
          )}
        </>
      );
    }
  )
);
